"use client";

import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import Image from "next/image";
import styles from "./NflWeek1Banner.module.css";

export default function NflWeek1Banner({ placement = "desktop" }) {
  const [open, setOpen] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const closeRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    setTimeout(() => closeRef.current?.focus(), 0);
    const onKey = (e) => e.key === "Escape" && handleClose();
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
      setLeaving(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleClose = () => {
    setLeaving(true);
    setTimeout(() => {
      setOpen(false);
      setLeaving(false);
    }, 220);
  };

  return (
    <>
      <button
        type="button"
        className={`${styles.bannerBtn} ${
          placement === "mobile"
            ? styles.bannerMobile
            : styles.bannerDesktop
        }`}
        onClick={() => setOpen(true)}
        aria-label="Open NFL Week 2 promotion details"
      >
        <Image
          src="/assets/nfl-goat-spins-promo.png"
          alt="NFL Week 2 - Keep the streak"
          width={2172}
          height={724}
          className={styles.bannerImg}
          priority
        />
      </button>

      {open
        ? createPortal(
            <div
              className={`${styles.backdrop} ${
                leaving ? styles.fadeOut : ""
              }`}
              onClick={handleClose}
              aria-hidden="true"
            >
              <div
                className={`${styles.modal} ${
                  leaving ? styles.modalOut : ""
                }`}
                role="dialog"
                aria-modal="true"
                aria-labelledby="nfl-week2-title"
                onClick={(e) => e.stopPropagation()}
              >
                <div className={styles.header}>
                  <h3 id="nfl-week2-title">NFL WEEK 2</h3>

                  <button
                    ref={closeRef}
                    type="button"
                    className={styles.close}
                    onClick={handleClose}
                    aria-label="Close"
                  >
                    ×
                  </button>
                </div>

                <div className={styles.content}>
                  <p className={styles.heroLine}>KEEP THE STREAK</p>

                  <p
                    className={`${styles.heroLine} ${styles.heroTouchdown}`}
                  >
                    3 GAMES. 3 PICKS. BIGGER REWARDS. 🏈
                  </p>

                  <p>Week 1 was just the warm-up.</p>

                  <p>
                    NFL Week 2 is bringing a <strong>bigger bonus</strong>,{" "}
                    <strong>bigger Free Spin rewards</strong>, and three
                    prime-time chances to keep your streak alive.
                  </p>

                  <h4>EVERY FEATURED WEEK 2 MATCH UNLOCKS</h4>

                  <p className={styles.emphasis}>
                    155% NO MAX CASHOUT
                  </p>

                  <p className={styles.emphasis}>30x WAGERING</p>

                  <p>
                    Pick the team you&apos;re backing and redeem their code.
                  </p>

                  <p className={styles.emphasis}>
                    Same offer — your team, your choice.
                  </p>

                  <h4>🏈 HOW THE STREAK WORKS</h4>

                  <p>
                    Week 2 brings <strong>3 featured prime-time offers</strong>.
                  </p>

                  <p>
                    Every qualifying offer you redeem moves your streak forward
                    and unlocks a bigger Free Spins reward.
                  </p>

                  <p className={styles.emphasis}>
                    KEEP PLAYING. KEEP EARNING.
                  </p>

                  <ul className={styles.rewards}>
                    <li>
                      <span>🏈 1ST REDEEM</span>
                      <b>25 FREE SPINS</b>
                    </li>

                    <li>
                      <span>🏈 2ND REDEEM</span>
                      <b>+50 FREE SPINS</b>
                    </li>

                    <li className={styles.touchdownReward}>
                      <span>🏆 3RD REDEEM</span>
                      <b>+100 FREE SPINS</b>
                    </li>
                  </ul>

                  <p className={styles.totalLine}>
                    Complete all 3 Week 2 picks and earn{" "}
                    <strong>175 FREE SPINS IN TOTAL 🏆</strong>
                  </p>

                  <h4>YOUR WEEK 2 PLAYBOOK</h4>

                  <div className={styles.down}>
                    <strong>
                      GAME #1 • THURSDAY NIGHT FOOTBALL
                    </strong>

                    <span>LIONS vs BILLS</span>

                    <p>
                      Start your Week 2 streak and unlock your first{" "}
                      <strong>25 Free Spins</strong>.
                    </p>
                  </div>

                  <div className={styles.down}>
                    <strong>
                      GAME #2 • SUNDAY NIGHT FOOTBALL
                    </strong>

                    <span>COLTS vs CHIEFS</span>

                    <p>
                      Keep the streak alive and unlock another{" "}
                      <strong>50 Free Spins</strong>.
                    </p>
                  </div>

                  <div className={`${styles.down} ${styles.downFinal}`}>
                    <strong>
                      GAME #3 • MONDAY NIGHT FOOTBALL
                    </strong>

                    <span>GIANTS vs RAMS</span>

                    <p>
                      Complete the full Week 2 streak and unlock the final{" "}
                      <strong>100 Free Spins</strong>.
                    </p>
                  </div>

                  <h4>🏈 REMEMBER</h4>

                  <ul className={styles.terms}>
                    <li>
                      Each featured Week 2 offer counts as one qualifying
                      redeem.
                    </li>

                    <li>
                      Each team code unlocks the same{" "}
                      <strong>155% No Max Cashout</strong> offer.
                    </li>

                    <li>
                      Wagering requirement: <strong>30x</strong>.
                    </li>

                    <li>
                      Rewards increase with every qualifying redemption.
                    </li>

                    <li>
                      Complete all three featured offers to earn{" "}
                      <strong>175 Free Spins in total</strong>.
                    </li>

                    <li>
                      Each featured offer can count once toward your Week 2
                      streak.
                    </li>

                    <li>Individual promotional terms apply.</li>
                  </ul>

                  <div className={styles.ctaBlock}>
                    <p className={styles.ctaTitle}>NFL WEEK 2</p>

                    <p className={styles.ctaTitle}>
                      READY TO KEEP THE STREAK ALIVE?
                    </p>

                    <p className={styles.emphasis}>
                      PICK YOUR TEAM. REDEEM. KEEP WINNING. 🏈
                    </p>
                  </div>

                  <p className={styles.legal}>
                    155% No Max Cashout offer is subject to 30x wagering and
                    individual promotional terms. Free Spins rewards are based
                    on qualifying Week 2 redemptions. Terms &amp; Conditions
                    apply.
                  </p>
                </div>

                <div className={styles.actions}>
                  <button
                    type="button"
                    className={styles.secondary}
                    onClick={handleClose}
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>,
            document.body
          )
        : null}
    </>
  );
}